import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ShieldExclamationIcon,
  ArrowLeftIcon,
  PlusIcon,
  XCircleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { RichTextEditor } from '@/components/ui/RichTextEditor';
import { cn } from '@/utils/cn';
import { threatIntelligenceService } from '@/services/threat-intelligence.service';
import toast from 'react-hot-toast';

export interface ThreatAssessmentFormData {
  title: string;
  description: string;
  threatLevel: 'low' | 'medium' | 'high' | 'critical';
  confidence: 'low' | 'medium' | 'high';
  category: 'cyber' | 'physical' | 'personnel' | 'reputation' | 'financial';
  sources: string[];
  targetedAssets: string[];
  indicators: string[];
  recommendations: string[];
  analyst: string;
  status: 'draft' | 'review' | 'published' | 'archived';
}

interface FormErrors {
  title?: string;
  description?: string;
  analyst?: string;
  sources?: string;
  targetedAssets?: string;
  recommendations?: string;
  general?: string;
}

export default function CreateThreatAssessment() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ThreatAssessmentFormData>({
    title: '',
    description: '',
    threatLevel: 'medium',
    confidence: 'medium',
    category: 'cyber',
    sources: [],
    targetedAssets: [],
    indicators: [],
    recommendations: [],
    analyst: '',
    status: 'draft',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Temporary input states for array fields
  const [currentSource, setCurrentSource] = useState('');
  const [currentAsset, setCurrentAsset] = useState('');
  const [currentIndicator, setCurrentIndicator] = useState('');
  const [currentRecommendation, setCurrentRecommendation] = useState('');

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    // Strip HTML tags for validation
    const plainTextDescription = formData.description.replace(/<[^>]*>/g, '').trim();
    if (!plainTextDescription) {
      newErrors.description = 'Description is required';
    }
    if (!formData.analyst.trim()) {
      newErrors.analyst = 'Analyst name is required';
    }
    if (formData.sources.length === 0) {
      newErrors.sources = 'At least one intelligence source is required';
    }
    if (formData.targetedAssets.length === 0) {
      newErrors.targetedAssets = 'At least one targeted asset is required';
    }
    if (formData.recommendations.length === 0) {
      newErrors.recommendations = 'At least one recommendation is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      await threatIntelligenceService.createThreatAssessment(formData);
      toast.success('Threat assessment created successfully');
      setSuccess(true);

      // Redirect to threats list after a short delay
      setTimeout(() => {
        navigate('/security/threats');
      }, 2000);
    } catch (error: unknown) {
      const err = error as Error;
      console.error('Failed to create threat assessment:', err);
      setErrors({
        general: err.message || 'Failed to create threat assessment. Please try again.',
      });
      toast.error('Failed to create threat assessment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof ThreatAssessmentFormData, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  // Array field handlers
  const handleAddSource = () => {
    if (currentSource.trim()) {
      setFormData((prev) => ({
        ...prev,
        sources: [...prev.sources, currentSource.trim()],
      }));
      setCurrentSource('');
      if (errors.sources) {
        setErrors((prev) => ({ ...prev, sources: undefined }));
      }
    }
  };

  const handleRemoveSource = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      sources: prev.sources.filter((_, i) => i !== index),
    }));
  };

  const handleAddAsset = () => {
    if (currentAsset.trim()) {
      setFormData((prev) => ({
        ...prev,
        targetedAssets: [...prev.targetedAssets, currentAsset.trim()],
      }));
      setCurrentAsset('');
      if (errors.targetedAssets) {
        setErrors((prev) => ({ ...prev, targetedAssets: undefined }));
      }
    }
  };

  const handleRemoveAsset = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      targetedAssets: prev.targetedAssets.filter((_, i) => i !== index),
    }));
  };

  const handleAddIndicator = () => {
    if (currentIndicator.trim()) {
      setFormData((prev) => ({
        ...prev,
        indicators: [...prev.indicators, currentIndicator.trim()],
      }));
      setCurrentIndicator('');
    }
  };

  const handleRemoveIndicator = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      indicators: prev.indicators.filter((_, i) => i !== index),
    }));
  };

  const handleAddRecommendation = () => {
    if (currentRecommendation.trim()) {
      setFormData((prev) => ({
        ...prev,
        recommendations: [...prev.recommendations, currentRecommendation.trim()],
      }));
      setCurrentRecommendation('');
      if (errors.recommendations) {
        setErrors((prev) => ({ ...prev, recommendations: undefined }));
      }
    }
  };

  const handleRemoveRecommendation = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      recommendations: prev.recommendations.filter((_, i) => i !== index),
    }));
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' },
    },
  };

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="p-4 sm:p-6"
      >
        <Card className="max-w-md mx-auto mt-20">
          <CardContent className="p-12 text-center">
            <CheckCircleIcon className="w-16 h-16 text-success-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-secondary-900 dark:text-white mb-2">
              Threat Assessment Created Successfully
            </h2>
            <p className="text-secondary-600 dark:text-secondary-400 mb-6">
              Your threat assessment has been created and saved. You will be redirected to the threat intelligence page shortly.
            </p>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => navigate('/security/threats')}
                className="flex-1"
              >
                View All Threats
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="p-4 sm:p-6 space-y-6"
    >
      {/* Header */}
      <motion.div variants={itemVariants}>
        <div className="flex items-center space-x-4 mb-6">
          <Link
            to="/security/threats"
            className="p-2 rounded-lg bg-secondary-100 dark:bg-secondary-800 hover:bg-secondary-200 dark:hover:bg-secondary-700 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 text-secondary-600 dark:text-secondary-400" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
              Add Threat Assessment
            </h1>
            <p className="text-secondary-600 dark:text-secondary-400 mt-1">
              Create a new threat assessment with detailed analysis and recommendations
            </p>
          </div>
        </div>
      </motion.div>

      {/* Form */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <ShieldExclamationIcon className="w-5 h-5 text-primary-600" />
              <span>Threat Assessment Information</span>
            </CardTitle>
            <CardDescription>
              Please provide detailed information about the threat. All fields marked with * are required.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* General Error */}
              {errors.general && (
                <div className="p-4 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <ShieldExclamationIcon className="w-5 h-5 text-danger-600 dark:text-danger-400" />
                    <p className="text-danger-800 dark:text-danger-200">{errors.general}</p>
                  </div>
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Title <span className="text-danger-500">*</span>
                </label>
                <Input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="e.g., Ransomware Campaign Targeting Financial Sector"
                  className={cn('w-full', errors.title && 'border-danger-300 dark:border-danger-600')}
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-danger-600">{errors.title}</p>
                )}
              </div>

              {/* Description */}
              <RichTextEditor
                label="Description"
                required
                value={formData.description}
                onChange={(html) => handleChange('description', html)}
                placeholder="Provide a detailed description of the threat, including context, indicators, and potential impact..."
                error={errors.description}
                minHeight={150}
              />

              {/* Dropdowns Row 1 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Threat Level <span className="text-danger-500">*</span>
                  </label>
                  <select
                    value={formData.threatLevel}
                    onChange={(e) =>
                      handleChange('threatLevel', e.target.value)
                    }
                    className="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Confidence Level <span className="text-danger-500">*</span>
                  </label>
                  <select
                    value={formData.confidence}
                    onChange={(e) =>
                      handleChange('confidence', e.target.value)
                    }
                    className="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>

              {/* Dropdowns Row 2 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Category <span className="text-danger-500">*</span>
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) =>
                      handleChange('category', e.target.value)
                    }
                    className="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="cyber">Cyber</option>
                    <option value="physical">Physical</option>
                    <option value="personnel">Personnel</option>
                    <option value="reputation">Reputation</option>
                    <option value="financial">Financial</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Status <span className="text-danger-500">*</span>
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      handleChange('status', e.target.value)
                    }
                    className="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="draft">Draft</option>
                    <option value="review">Under Review</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>

              {/* Analyst */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Analyst <span className="text-danger-500">*</span>
                </label>
                <Input
                  type="text"
                  value={formData.analyst}
                  onChange={(e) => handleChange('analyst', e.target.value)}
                  placeholder="e.g., John Smith"
                  className={cn('w-full', errors.analyst && 'border-danger-300')}
                />
                {errors.analyst && (
                  <p className="mt-1 text-sm text-danger-600">{errors.analyst}</p>
                )}
              </div>

              {/* Intelligence Sources */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Intelligence Sources <span className="text-danger-500">*</span>
                </label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={currentSource}
                    onChange={(e) => setCurrentSource(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSource())}
                    placeholder="Enter source name and press Enter"
                    className={cn('flex-1', errors.sources && 'border-danger-300')}
                  />
                  <Button type="button" onClick={handleAddSource} variant="outline" size="sm">
                    <PlusIcon className="w-4 h-4" />
                  </Button>
                </div>
                {errors.sources && (
                  <p className="mt-1 text-sm text-danger-600">{errors.sources}</p>
                )}
                {formData.sources.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {formData.sources.map((source, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300 rounded-lg text-sm"
                      >
                        {source}
                        <button
                          type="button"
                          onClick={() => handleRemoveSource(index)}
                          className="ml-1 hover:text-primary-900 dark:hover:text-primary-100"
                        >
                          <XCircleIcon className="w-4 h-4" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Targeted Assets */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Targeted Assets <span className="text-danger-500">*</span>
                </label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={currentAsset}
                    onChange={(e) => setCurrentAsset(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddAsset())}
                    placeholder="Enter asset name and press Enter"
                    className={cn('flex-1', errors.targetedAssets && 'border-danger-300')}
                  />
                  <Button type="button" onClick={handleAddAsset} variant="outline" size="sm">
                    <PlusIcon className="w-4 h-4" />
                  </Button>
                </div>
                {errors.targetedAssets && (
                  <p className="mt-1 text-sm text-danger-600">{errors.targetedAssets}</p>
                )}
                {formData.targetedAssets.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {formData.targetedAssets.map((asset, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-danger-100 text-danger-800 dark:bg-danger-900/20 dark:text-danger-300 rounded-lg text-sm"
                      >
                        {asset}
                        <button
                          type="button"
                          onClick={() => handleRemoveAsset(index)}
                          className="ml-1 hover:text-danger-900 dark:hover:text-danger-100"
                        >
                          <XCircleIcon className="w-4 h-4" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Indicators of Compromise (Optional) */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Indicators of Compromise (IOCs)
                </label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={currentIndicator}
                    onChange={(e) => setCurrentIndicator(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddIndicator())}
                    placeholder="Enter IOC (IP, hash, domain, etc.) and press Enter"
                  />
                  <Button type="button" onClick={handleAddIndicator} variant="outline" size="sm">
                    <PlusIcon className="w-4 h-4" />
                  </Button>
                </div>
                {formData.indicators.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {formData.indicators.map((indicator, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300 rounded-lg text-sm font-mono"
                      >
                        {indicator}
                        <button
                          type="button"
                          onClick={() => handleRemoveIndicator(index)}
                          className="ml-1 hover:text-warning-900 dark:hover:text-warning-100"
                        >
                          <XCircleIcon className="w-4 h-4" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Recommendations */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Security Recommendations <span className="text-danger-500">*</span>
                </label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={currentRecommendation}
                    onChange={(e) => setCurrentRecommendation(e.target.value)}
                    onKeyPress={(e) =>
                      e.key === 'Enter' && (e.preventDefault(), handleAddRecommendation())
                    }
                    placeholder="Enter recommendation and press Enter"
                    className={cn('flex-1', errors.recommendations && 'border-danger-300')}
                  />
                  <Button type="button" onClick={handleAddRecommendation} variant="outline" size="sm">
                    <PlusIcon className="w-4 h-4" />
                  </Button>
                </div>
                {errors.recommendations && (
                  <p className="mt-1 text-sm text-danger-600">{errors.recommendations}</p>
                )}
                {formData.recommendations.length > 0 && (
                  <div className="space-y-2 mt-3">
                    {formData.recommendations.map((recommendation, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-2 p-3 bg-success-50 dark:bg-success-900/20 rounded-lg"
                      >
                        <span className="flex-shrink-0 w-6 h-6 bg-success-600 text-white dark:bg-success-500 rounded-full flex items-center justify-center text-xs font-semibold mt-0.5">
                          {index + 1}
                        </span>
                        <span className="flex-1 text-sm text-secondary-900 dark:text-white">
                          {recommendation}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveRecommendation(index)}
                          className="flex-shrink-0 text-secondary-500 hover:text-danger-600"
                        >
                          <XCircleIcon className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-secondary-200 dark:border-secondary-700">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/security/threats')}
                  disabled={isSubmitting}
                  className="w-full sm:w-auto"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto bg-primary-600 hover:bg-primary-700"
                >
                  {isSubmitting ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Creating Assessment...</span>
                    </div>
                  ) : (
                    'Create Assessment'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
}
